import { BookCallButton, ButtonLink } from '@/components/ui/Button'
import { PageSeo } from '@/components/seo/PageSeo'
import { sitePages } from '@/config/pages'
import { team } from '@/config/team'

/** The longer introductions on this page. Names and titles come from the team list. */
const introductions: Record<string, { lead: string; bio: string }> = {
  'Karl Paadam': {
    lead: 'Tech founder turned PwC partner, and back.',
    bio: 'Karl built Estonia’s largest Tax & Legal practice at PwC, starting with no clients, then led acquisitions and integrations across Central and Eastern Europe during ten years as a partner. He has two technology exits, most recently Yummy, acquired by Ruokaboksi in 2024.',
  },
  'Pascal Noth': {
    lead: 'Two decades in private equity, turning founder-led businesses into platforms.',
    bio: 'Youngest partner at Palamon Capital Partners, then Head of Private Equity Healthcare & Life Sciences at Partners Group. Many of his investments were roll-ups, backing founders who stayed in charge while their businesses grew through acquisitions.',
  },
  'Peter Potapov': {
    lead: 'Has built accounting production systems twice, before and after large language models.',
    bio: 'At Osome he led engineering and machine learning, including an accounting factory where models prepare the work and accountants handle judgment and exceptions. He then led technology at Kick, a US bookkeeping company backed by the OpenAI Startup Fund and General Catalyst.',
  },
}

/* A calm, text-led page: each row is a small label on the left and the words on the right. */
export const AboutPage = () => (
  <>
    <PageSeo page={sitePages.about} />
    <div className="a2">
      <div className="nav-space" aria-hidden="true" />
      <div className="x-wrap">
        <section className="a2-row a2-row--top" aria-labelledby="a-title">
          <p className="x-eyebrow x-eyebrow--ink a2-label">Mission</p>
          <div className="a2-copy">
            <h1 id="a-title" className="a2-title">
              Saving humanity from boring work
            </h1>
            <p className="a2-text a2-text--lead">
              We’re ending boring work, starting with accounting, so accountants stop preparing and start advising.{' '}
              <strong>The future of accounting is human.</strong> That is not a reassurance. That is the design.
            </p>
          </div>
        </section>

        <section id="a-vision" className="a2-row" aria-labelledby="a-vision-title">
          <p className="x-eyebrow x-eyebrow--ink a2-label">Vision</p>
          <div className="a2-copy">
            <h2 id="a-vision-title" className="a2-statement">
              The financial platform for America’s small businesses
            </h2>
            <p className="a2-text">
              Today we buy into small, tech-savvy accounting firms and move the boring work into software, with an accountant
              approving everything that reaches a client. That frees the CPA’s time for what they were always for: advising, as
              the most trusted person a small business has. Then those CPAs can offer what their clients need, from treasury and
              credit to insurance, retirement and wealth, through the one relationship that already holds the trust. Accounting is
              the entry point, not the destination.
            </p>
            <ButtonLink to={sitePages.manifesto.path} variant="line" className="a2-btn">
              Read the full manifesto
            </ButtonLink>
          </div>
        </section>

        <section id="a-team" className="a2-row" aria-labelledby="a-team-title">
          <h2 id="a-team-title" className="x-eyebrow x-eyebrow--ink a2-label">
            Who we are
          </h2>
          <div className="a2-copy a2-people">
            <p className="a2-text a2-team-intro">
              Between us, we’ve built a professional-services network, a buy-and-build platform of professional practices, and
              AI production systems for accounting. United needs all three.
            </p>
            {team.map((person) => (
              <article key={person.name} className="a2-person">
                <h3>
                  {person.name} <span>{person.role}</span>
                </h3>
                <p className="a2-text">
                  <strong>{introductions[person.name].lead}</strong> {introductions[person.name].bio}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section id="a-next" className="a2-row a2-row--next" aria-labelledby="a-next-title">
          <p className="x-eyebrow x-eyebrow--ink a2-label">Next step</p>
          <div className="a2-copy">
            <h2 id="a-next-title" className="a2-statement">
              Start with a conversation about your firm
            </h2>
            <BookCallButton className="a2-btn" />
          </div>
        </section>
      </div>
    </div>
  </>
)
